import { dungApiThat, goiApi } from '../api/http';
import { nguCanhMau } from '../../mock/ngu-canh';
import { mayChuNguoiDung } from '../../mock/nguoi-dung';
import { mockRequest, mockState } from '../../mock/transport';
import { useAuthStore } from '../../stores/auth';
import type { MyContext } from '../../modules/auth/nghiep-vu/types';
import type {
  EmailNhanThuChanged,
  InviteResent,
  InvitedUser,
  MfaRequiredUsers,
  MfaRequiredUsersUpdate,
  TenantUserBriefPage,
  UserInvite,
  UserPage,
  UserProfile,
  UserStatus,
  UserUpdate,
} from '../../modules/nguoi-dung/types';

const GOC = '/api/v1/users';

const duongDan = (id: string, hanhDong?: string) =>
  `${GOC}/${encodeURIComponent(id)}${hanhDong ? `/${hanhDong}` : ''}`;

/**
 * Chạy máy chủ giả **sau** nhịp chờ và các trạng thái `?mock=` của
 * `mockRequest`: lỗi nghiệp vụ tới cùng độ trễ với lỗi thật.
 */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

const TRANG_RONG: UserPage = { items: [], next_cursor: null };
const TAP_RONG: MfaRequiredUsers = { user_ids: [] };

export interface ThamSoDanhSach {
  limit: number;
  cursor?: string;
  status?: UserStatus;
}

/**
 * Truy cập dữ liệu người dùng — `user-service`, qua Gateway. `tenant_id` lấy từ
 * token, không bao giờ gửi tham số.
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/nguoi-dung.ts`).
 */
export const nguoiDungRepository = {
  /**
   * `GET /api/v1/users/me/context` — quyền hiệu lực và dịch vụ trong gói của
   * tenant (ADR-0034). `tenant_id`, `user_id` lấy từ token, không gửi tham số.
   *
   * Nhánh dữ liệu mẫu **không** đi qua `mockRequest`: `?mock=loading|error` là
   * để soát trạng thái của từng màn, còn ngữ cảnh quyền bọc cả ứng dụng — diễn
   * lỗi ở đây thì không màn nào soát được nữa.
   */
  nguCanhCuaToi: (vaiTroLop1: readonly string[]): Promise<MyContext> =>
    dungApiThat()
      ? goiApi<MyContext>('/api/v1/users/me/context')
      : Promise.resolve(nguCanhMau(vaiTroLop1)),

  /**
   * `GET /api/v1/users` — một trang. `?mock=filtered-empty` diễn ở đây: có bộ
   * lọc thì trả rỗng, nên view phân biệt hai kiểu rỗng bằng chính state bộ lọc
   * của nó, đúng như khi chạy API thật.
   */
  danhSach: ({ limit, cursor, status }: ThamSoDanhSach): Promise<UserPage> => {
    if (dungApiThat()) return goiApi<UserPage>(GOC, { query: { limit, cursor, status } });
    if (mockState() === 'filtered-empty' && status) return mockRequest(TRANG_RONG);
    return mockRequest(mayChuNguoiDung.danhSach({ limit, cursor, status }), TRANG_RONG);
  },

  /** `PATCH /api/v1/users/{id}` — chỉ gửi trường đổi; `status` để khoá, mở khoá. */
  sua: (id: string, du: UserUpdate): Promise<UserProfile> =>
    dungApiThat()
      ? goiApi<UserProfile>(duongDan(id), { method: 'PATCH', body: du })
      : giaLap(() => mayChuNguoiDung.sua(id, du)),

  /** `POST /api/v1/users/invite` — `khoa` là `Idempotency-Key`, do form giữ. */
  moi: (du: UserInvite, khoa: string): Promise<InvitedUser> =>
    dungApiThat()
      ? goiApi<InvitedUser>(`${GOC}/invite`, { method: 'POST', body: du, idempotencyKey: khoa })
      : giaLap(() => mayChuNguoiDung.moi(du)),

  /** `POST /api/v1/users/{id}/resend-invite` — tối đa 5 lần mỗi ngày cho một người. */
  guiLaiLoiMoi: (id: string): Promise<InviteResent> =>
    dungApiThat()
      ? goiApi<InviteResent>(duongDan(id, 'resend-invite'), { method: 'POST' })
      : giaLap(() => mayChuNguoiDung.guiLai(id)),

  /** `GET /api/v1/users/mfa-required` — ai có cờ bắt buộc xác thực hai lớp riêng. */
  batBuocMfa: (): Promise<MfaRequiredUsers> =>
    dungApiThat()
      ? goiApi<MfaRequiredUsers>(`${GOC}/mfa-required`)
      : mockRequest(undefined, TAP_RONG).then((rong) => rong ?? mayChuNguoiDung.batBuocMfa()),

  /**
   * `PUT /api/v1/users/mfa-required` — **thay cả tập**. Id lạ: `422` theo
   * `#/user_ids/<i>`; Keycloak không trả lời: `503`.
   */
  datBatBuocMfa: (du: MfaRequiredUsersUpdate): Promise<MfaRequiredUsers> =>
    dungApiThat()
      ? goiApi<MfaRequiredUsers>(`${GOC}/mfa-required`, { method: 'PUT', body: du })
      : giaLap(() => mayChuNguoiDung.datBatBuocMfa(du.user_ids)),

  /**
   * `POST /api/v1/users/{id}/password-reset` — `202`: Keycloak gửi email kèm
   * liên kết đặt mật khẩu mới. `409` chưa nhận lời mời hay đang bị khoá, `429`
   * gửi quá nhiều trong ngày, `503` máy chủ email lỗi.
   */
  guiDatLaiMatKhau: (id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(duongDan(id, 'password-reset'), { method: 'POST' })
      : giaLap(() => mayChuNguoiDung.guiDatLaiMatKhau(id)),

  /** `DELETE /api/v1/users/{id}/login-lock` — `204`: gỡ khoá tạm vì nhập sai mật khẩu nhiều lần. */
  moKhoaDangNhap: (id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(duongDan(id, 'login-lock'), { method: 'DELETE' })
      : giaLap(() => mayChuNguoiDung.moKhoaDangNhap(id)),

  /**
   * `DELETE /api/v1/users/{id}/otp` — `204`: xoá ứng dụng xác thực và mã khôi
   * phục, đăng xuất người đó. Gọi cho chính mình: `403 PERMISSION_DENIED`.
   */
  /**
   * `GET /api/v1/tenants/{id}/users` (tenant-service, `PLATFORM_ADMIN`, `AUDITOR`) — người dùng rút gọn của một tenant
   * cho cổng nền tảng. `search` lọc tên hiển thị ở server. Mock dùng người dùng của tổ chức mẫu cho mọi tenant;
   * `?mock=filtered-empty` trả rỗng khi có chuỗi tìm.
   */
  cuaTenant: (
    tenantId: string,
    { search = '', limit, cursor }: { search?: string; limit: number; cursor?: string | null },
  ): Promise<TenantUserBriefPage> => {
    if (dungApiThat()) {
      return goiApi<TenantUserBriefPage>(`/api/v1/tenants/${encodeURIComponent(tenantId)}/users`, {
        query: { search: search.trim() || undefined, limit, cursor: cursor ?? undefined },
      });
    }
    const rong: TenantUserBriefPage = { items: [], next_cursor: null };
    const q = search.trim().toLowerCase();
    if (mockState() === 'filtered-empty' && q) return mockRequest(rong);
    const tat = mayChuNguoiDung
      .danhSach({ limit: 1000 })
      .items.filter((u) => !q || u.display_name.toLowerCase().includes(q))
      .map((u) => ({
        user_id: u.user_id,
        display_name: u.display_name,
        email: u.email,
        email_nhan_thu: u.email_nhan_thu ?? u.email,
        status: u.status,
      }));
    const batDau = cursor ? Number(cursor) : 0;
    return mockRequest(
      {
        items: tat.slice(batDau, batDau + limit),
        next_cursor: batDau + limit < tat.length ? String(batDau + limit) : null,
      },
      rong,
    );
  },

  /**
   * `PUT /api/v1/tenants/{tenantId}/users/{id}/email-nhan-thu` (ADR-0101) — do user-service phục vụ dưới
   * `/tenants/{id}` như khoá tạm. `422 EMAIL_NOT_INVITABLE` khi địa chỉ đã thuộc tài khoản khác; gọi cho chính mình
   * `403`. Không tạo tài nguyên nên không có `Idempotency-Key`.
   */
  doiEmailNhanThu: (tenantId: string, id: string, email: string): Promise<EmailNhanThuChanged> =>
    dungApiThat()
      ? goiApi<EmailNhanThuChanged>(
          `/api/v1/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(id)}/email-nhan-thu`,
          { method: 'PUT', body: { email_nhan_thu: email } },
        )
      : giaLap(() => mayChuNguoiDung.doiEmailNhanThu(id, email, useAuthStore.getState().user?.id)),

  datLaiMfa: (id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(duongDan(id, 'otp'), { method: 'DELETE' })
      : giaLap(() => mayChuNguoiDung.datLaiMfa(id, useAuthStore.getState().user?.id)),
};
